import { useState, useEffect } from "react";
import { api, useAuth } from "../Contexts/AuthContext";
import "../styles/team.css";

const STATUS_LABEL = { online: "Online", offline: "Offline", busy: "Busy" };

const initials = (name) =>
  (name || "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";

const EMPTY_FORM = {
  username: "", first_name: "", last_name: "", designation: "", department: "", 
  email: "", phone_number: "", address: "", state: "", pincode: "", 
  date_of_birth: "", date_joined_company: "", gender: "Male", skills: "", 
  bio: "", status: "online", feedback_score: 70, profile_picture: "", resume_url: "",
  rbac_roles: [], base_salary: 0, reporting_to: null,
  official_telephony_number: "", is_active: true
};

export default function Team() {
  const { user } = useAuth();
  const [mode, setMode] = useState("members"); // "members" or "groups"
  const [search, setSearch] = useState("");
  const [dept,   setDept]   = useState("All");
  const [membersData, setMembersData] = useState([]);
  const [teamsData, setTeamsData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgConfig, setOrgConfig] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  
  const [form, setForm] = useState(EMPTY_FORM);
  const [teamForm, setTeamForm] = useState({ name: "", lead: "", members: [] });

  const fetchOrgConfig = async () => {
    try {
      const response = await api.get("organizations/me/");
      setOrgConfig(response.data.onboarding_config);
    } catch (error) {
      console.error("Failed to fetch org config", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get("organizations/teams/");
      setTeamsData(response.data);
    } catch (error) {
      console.error("Failed to fetch teams", error);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await api.get("accounts/users/");
      const mapped = response.data.map(u => ({
        ...u,
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
        role: u.designation || 'Specialist',
        dept: u.department || 'Operations',
        mapped_roles: u.rbac_roles_details?.map(r => r.name).join(", ") || "No Roles",
        image: u.profile_picture,
        performance: u.performance || 0,
        base_salary: Math.round(Number(u.base_salary) || 0),
        reporting_to: u.reporting_to,
        official_telephony_number: u.official_telephony_number || "",
        status: u.status || 'offline'
      }));
      setMembersData(mapped);
    } catch (error) {
      console.error("Failed to fetch team members", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get("rbac/roles/?custom_only=true");
      setAllRoles(response.data);
    } catch (error) {
      console.error("Failed to fetch roles", error);
    }
  };

  const fmtINR = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(Number(amount) || 0));

  useEffect(() => {
    fetchMembers();
    fetchRoles();
    fetchTeams();
    fetchOrgConfig();
  }, []);

  const handleTeamSave = async () => {
    try {
      if (editId) {
        await api.patch(`organizations/teams/${editId}/`, teamForm);
      } else {
        await api.post("organizations/teams/", teamForm);
      }
      setShowTeamForm(false);
      fetchTeams();
    } catch (error) {
      console.error("Failed to save team", error);
      alert("Error saving team: " + (error.response?.data?.detail || "Check all fields"));
    }
  };

  const deleteTeam = async (id) => {
    if(!window.confirm("Delete this team?")) return;
    try {
      await api.delete(`organizations/teams/${id}/`);
      fetchTeams();
    } catch (error) {
       console.error("Delete failed", error);
    }
  };

  // Calculations are now handled by the backend for consistency

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      
      const editingMember = editId ? membersData.find(m => m.id === editId) : null;

      // Validation: Handle mandatory fields
      if (!form.first_name || !form.email) {
        alert("Please fill in all required fields (First Name, Email).");
        return;
      }

      if (!editingMember && !form.username) {
        alert("Please set a username for the new member.");
        return;
      }

      if (!editingMember && !form.password) {
        alert("Please set a password for the new member.");
        return;
      }

      const { 
        rbac_roles_details, organization_details, all_permissions, 
        performance, performance_breakdown, is_admin_priority,
        id, mapped_roles, role, dept, initials, name,
        image, // frontend uses image, backend uses profile_picture
        ...cleanForm 
      } = form;
      
      const payload = {
        ...cleanForm,
        base_salary: Math.round(Number(form.base_salary) || 0),
        date_of_birth: form.date_of_birth || null,
        date_joined_company: form.date_joined_company || null,
        username: form.username || form.email.split('@')[0] || `user_${Date.now()}`
      };

      // Sanitize payload: 
      // 1. Remove profile_picture if it's a string (URL from backend) to avoid file validation error
      if (typeof payload.profile_picture === 'string') {
        delete payload.profile_picture;
      }
      // 2. Remove password if editing and blank
      if (editingMember && !form.password) {
        delete payload.password;
      }

      if (editingMember) {
        await api.put(`accounts/users/${editingMember.id}/`, payload);
      } else {
        await api.post("accounts/users/", payload);
      }
      
      setShowForm(false);
      fetchMembers();
    } catch (error) {
      console.error("Failed to save employee", error);
      const detail = error.response?.data?.detail || JSON.stringify(error.response?.data) || "Unknown error";
      alert("Error saving employee: " + detail);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    try {
      await api.delete(`accounts/users/${id}/`);
      fetchMembers();
    } catch (error) {
      console.error("Failed to delete member", error);
    }
  };

  const handleToggleActive = async (member) => {
    const actionText = member.is_active !== false ? "deactivate" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${actionText} this member?`)) return;
    try {
      await api.put(`accounts/users/${member.id}/`, { ...member, is_active: member.is_active === false });
      fetchMembers();
    } catch (error) {
      console.error("Failed to toggle member activation", error);
      alert("Failed to update status: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const depts = ["All", ...Array.from(new Set(membersData.map((m) => m.dept)))];

  const members = membersData.filter((m) => {
    const matchDept   = dept === "All" || m.dept === dept;
    const matchSearch = m.name.toLowerCase().includes(search.toLowerCase()) ||
                        m.username.toLowerCase().includes(search.toLowerCase()) ||
                        m.role.toLowerCase().includes(search.toLowerCase());
    return matchDept && matchSearch;
  });

  const onlineCount = membersData.filter((m) => m.status === "online").length;
  const hasPerm = (perm) => user?.all_permissions?.includes(perm) || user?.is_superuser;
  const canAddMember = hasPerm('add_team_member') || hasPerm('manage_teams');
  const canManageSquads = hasPerm('manage_squads');

  return (
    <main className="main" style={{ marginLeft: 0, padding: 0 }}>
      <div className="page-head">
        <div>
          <div className="page-title">👥 Team Members</div>
          <div className="page-sub">Manage your team effectively</div>
        </div>
        {canAddMember && (
          <button className="add-btn" onClick={() => {
            setForm(EMPTY_FORM);
            setEditId(null);
            setShowForm(true);
          }}>
            ＋ Add Member
          </button>
        )}
      </div>

      <div className="stats-strip">
        <div className="strip-card">
          <div className="strip-ico t">👥</div>
          <div>
            <div className="strip-num">{membersData.length}</div>
            <div className="strip-lbl">Total Members</div>
          </div>
        </div>
        <div className="strip-card">
          <div className="strip-ico a">🟢</div>
          <div>
            <div className="strip-num">{onlineCount}</div>
            <div className="strip-lbl">Online Now</div>
          </div>
        </div>
        <div className="strip-card">
          <div className="strip-ico d">🏢</div>
          <div>
            <div className="strip-num">{depts.length - 1}</div>
            <div className="strip-lbl">Departments</div>
          </div>
        </div>
      </div>

      <div className="tab-nav" style={{ display: "flex", gap: "20px", marginBottom: "25px", borderBottom: "1px solid #e2e8f0" }}>
          <button 
            onClick={() => setMode("members")}
            style={{ 
              padding: "10px 5px", border: "none", background: "none", 
              color: mode === "members" ? "var(--green)" : "#64748b",
              fontWeight: 700, borderBottom: mode === "members" ? "3px solid var(--green)" : "3px solid transparent",
              transition: "all 0.2s"
            }}
          >
            Individual Members
          </button>
          <button 
            onClick={() => setMode("groups")}
            style={{ 
              padding: "10px 5px", border: "none", background: "none", 
              color: mode === "groups" ? "var(--green)" : "#64748b",
              fontWeight: 700, borderBottom: mode === "groups" ? "3px solid var(--green)" : "3px solid transparent",
              transition: "all 0.2s"
            }}
          >
            Organizational Teams (Squads)
          </button>
      </div>

      {mode === "members" ? (
        <>
          <div className="filter-bar">
            {/* ... search and dept filter ... */}
            <div className="search-wrap">
              🔍
              <input
                type="text"
                placeholder="Search by name or role…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ border: "none", outline: "none", background: "transparent", flex: 1, fontSize: 13, color: "var(--text)" }}
              />
            </div>

            <select className="dept-filter" value={dept} onChange={(e) => setDept(e.target.value)}>
              {depts.map((d) => (
                <option key={d} value={d}>{d === "All" ? "All Departments" : d}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "60px" }}>⌛ Loading team members...</div>
          ) : members.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#888", fontSize: 14 }}>
              😕 No members found.
            </div>
          ) : (
            <div className="team-grid">
              {members.map((m) => (
                <div className="member-card" key={m.id}>
                  <div className="card-stripe" />
                  <div className="card-body">
                    <div className="ava-wrap">
                      {m.image ? <img src={m.image} className="member-ava" alt="" /> : <div className="member-ava">{initials(m.name)}</div>}
                      <span className={`status-dot ${m.is_active === false ? "inactive" : m.status}`} title={m.is_active === false ? "Deactivated / Ex-Employee" : STATUS_LABEL[m.status]} />
                    </div>
                    <div className="member-info">
                      <div className="member-name">
                        {m.name}
                        {m.is_active === false && (
                          <span style={{ fontSize: '10px', background: '#fee2e2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px', fontWeight: 'bold', display: 'inline-block', border: '1px solid #fca5a5', textTransform: 'uppercase' }}>
                            Deactivated
                          </span>
                        )}
                      </div>
                      <div className="member-uname">@{m.username}</div>
                      <div className="member-role">{m.role}</div>
                      <div className="member-roles-list">🛡️ {m.mapped_roles}</div>
                      <div className="member-dept">🏢 {m.dept}</div>
                    </div>
                    <div className="member-performance">
                      <div className="perf-label">Performance</div>
                      <div className="perf-bar">
                        <div className="perf-fill" style={{ 
                          width: `${m.performance}%`,
                          background: m.performance > 80 ? "#22c55e" : m.performance > 60 ? "#f59e0b" : "#ef4444"
                        }} />
                      </div>
                      <div className="perf-text">{m.performance}%</div>
                    </div>
                  </div>
                  <div className="card-footer">
                    {hasPerm('manage_teams') && (
                      <>
                        <button className="card-action" onClick={() => { 
                            setForm({
                                ...EMPTY_FORM, 
                                ...m,
                                rbac_roles: m.rbac_roles && m.rbac_roles.length > 0
                                  ? m.rbac_roles.map(Number)
                                  : (m.rbac_roles_details || []).map(r => Number(r.id)),
                                base_salary: Math.round(Number(m.base_salary) || 0),
                                reporting_to: m.reporting_to || null,
                                password: "" // Reset password field for editing
                            }); 
                            setEditId(m.id); 
                            setShowForm(true); 
                        }}>✏️ Edit</button>
                        <button className="card-action" onClick={() => handleToggleActive(m)}>
                          {m.is_active === false ? "🟢 Enable" : "🔴 Disable"}
                        </button>
                        <button className="card-action" onClick={() => handleDelete(m.id)}>🗑 Delete</button>
                      </>
                    )}
                    <button className="card-action primary" onClick={() => setViewData(m)}>👁 View</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="groups-view">
             <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h3 style={{ fontSize: "18px", fontWeight: 800 }}>Defined Teams & Squads</h3>
                {canManageSquads && (
                  <button className="add-btn" style={{ padding: "7px 15px" }} onClick={() => {
                      setEditId(null);
                      setTeamForm({ name: "", lead: "", members: [] });
                      setShowTeamForm(true);
                  }}>＋ Create Team</button>
                )}
             </div>

             <div className="team-grid">
                {teamsData.map(team => (
                    <div className="member-card" key={team.id}>
                        <div className="card-stripe" style={{ background: "var(--green)" }} />
                        <div className="card-body" style={{ alignItems: "flex-start", textAlign: "left" }}>
                             <div style={{ fontSize: "16px", fontWeight: 800, marginBottom: "5px" }}>{team.name}</div>
                             <div style={{ fontSize: "12px", color: "var(--green)", fontWeight: 700, marginBottom: "15px" }}>👑 {team.lead_name || "No Lead Appointed"} (@{team.lead_username || 'unassigned'})</div>
                             
                             <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#94a3b8", fontWeight: 700, marginBottom: "8px" }}>Members ({team.member_count})</div>
                             <div className="team-member-stack" style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                {team.members.filter(mid => mid !== team.lead).map(mid => {
                                    const m = membersData.find(x => x.id === mid);
                                    return m ? (
                                        <div key={mid} style={{ width: "30px", height: "30px", borderRadius: "50%", background: "var(--yellow-lt)", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 800 }} title={m.name}>
                                            {initials(m.name)}
                                        </div>
                                    ) : null;
                                })}
                             </div>
                        </div>
                        <div className="card-footer">
                            {canManageSquads && (
                              <>
                                <button className="card-action" onClick={() => {
                                    setEditId(team.id);
                                    setTeamForm({ name: team.name, lead: team.lead, members: team.members });
                                    setShowTeamForm(true);
                                }}>✏️ Edit Team</button>
                                <button className="card-action" onClick={() => deleteTeam(team.id)}>🗑 Delete</button>
                              </>
                            )}
                        </div>
                    </div>
                ))}
             </div>
        </div>
      )}

      {showTeamForm && (
          <div className="modal-overlay">
              <div className="modal-box large">
                  <h3>{editId ? "Edit Team" : "Create New Team"}</h3>
                  <div className="form-grid" style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                      <div>
                          <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "5px" }}>Team Name</label>
                          <input 
                            style={{ width: "100%", padding: "10px" }}
                            placeholder="e.g. Sales Mumbai Squad" 
                            value={teamForm.name} 
                            onChange={(e) => setTeamForm({...teamForm, name: e.target.value})} 
                          />
                      </div>
                      <div>
                          <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "5px" }}>Appoint Team Lead</label>
                          <select 
                            style={{ width: "100%", padding: "10px" }}
                            value={teamForm.lead} 
                            onChange={(e) => setTeamForm({...teamForm, lead: e.target.value})}
                          >
                              <option value="">Select a Lead</option>
                              {membersData.map(m => (
                                  <option key={m.id} value={m.id}>{m.name} (@{m.username})</option>
                              ))}
                          </select>
                      </div>
                      <div>
                          <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "5px" }}>Assign Team Members</label>
                          <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px" }}>
                                {membersData.map(m => (
                                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "5px 0", cursor: "pointer", fontSize: "13px" }}>
                                        <input 
                                            type="checkbox" 
                                            checked={teamForm.members.includes(m.id)}
                                            onChange={(e) => {
                                                const current = [...teamForm.members];
                                                if(e.target.checked) setTeamForm({...teamForm, members: [...current, m.id]});
                                                else setTeamForm({...teamForm, members: current.filter(x => x !== m.id)});
                                            }}
                                        />
                                        {m.name} (@{m.username})
                                    </label>
                                ))}
                          </div>
                      </div>
                  </div>
                  <div className="modal-actions" style={{ marginTop: "20px" }}>
                      <button onClick={() => setShowTeamForm(false)}>Cancel</button>
                      <button className="add-btn" style={{ borderRadius: "8px" }} onClick={handleTeamSave}>Save Team Squad</button>
                  </div>
              </div>
          </div>
      )}

      {/* ... existing Member Form and View Modals ... */}
      {showForm && (
          // ... (existing showForm block) ...
        <div className="modal-overlay">
          <div className="modal-box large scroll">
            <h2>{editId ? "Edit Employee" : "Add Employee"}</h2>
            <div className="form-grid">
              {!editId && (
                <div className="form-field-full">
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Username <span style={{ color: '#ef4444' }}>*</span></label>
                  <input placeholder="Username" value={form.username} onChange={(e)=>setForm({...form,username:e.target.value})} />
                </div>
              )}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>First Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input placeholder="First Name" value={form.first_name} onChange={(e)=>setForm({...form,first_name:e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Last Name</label>
                <input placeholder="Last Name" value={form.last_name} onChange={(e)=>setForm({...form,last_name:e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Designation</label>
                <input placeholder="Designation" value={form.designation} onChange={(e)=>setForm({...form,designation:e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Department</label>
                <input placeholder="Department" value={form.department} onChange={(e)=>setForm({...form,department:e.target.value})} />
              </div>
              <div className="form-field-full">
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                <input placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Phone</label>
                <input placeholder="Phone" value={form.phone_number} onChange={(e)=>setForm({...form,phone_number:e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Password {!editId && <span style={{ color: '#ef4444' }}>*</span>}</label>
                <div style={{ position: "relative" }}>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    placeholder={editId ? "Leave blank to keep" : "Password"} 
                    value={form.password} 
                    onChange={(e)=>setForm({...form,password:e.target.value})} 
                    style={{ width: "100%", paddingRight: "40px" }}
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ 
                      position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer", fontSize: "16px",
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}
                  >
                    {showPassword ? "👁️" : "🔒"}
                  </button>
                </div>
              </div>
              <div className="form-field-full">
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Base Salary (Monthly)</label>
                <input placeholder="Base Salary (Monthly)" type="number" value={form.base_salary} onChange={(e)=>setForm({...form,base_salary:e.target.value})} />
              </div>
              <div className="form-field-full">
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Official Telephony Number (Callyzer)</label>
                <input placeholder="e.g. 919876543210" value={form.official_telephony_number} onChange={(e)=>setForm({...form,official_telephony_number:e.target.value})} />
                <small style={{ fontSize: '10px', color: '#6366f1' }}>Used to link mobile call logs to this employee record.</small>
              </div>
              <div className="form-field-full">
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>Address</label>
                <input placeholder="Address" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} />
              </div>
              <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})}>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="busy">Busy</option>
              </select>
              <div className="form-field-full">
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Manager Feedback Score (0-100)</label>
                <input 
                  type="range" min="0" max="100" 
                  value={form.feedback_score} 
                  onChange={(e)=>setForm({...form, feedback_score: parseInt(e.target.value)})} 
                  style={{ width: "100%", accentColor: "var(--green)" }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}>
                   <span>Critical (0)</span>
                   <span style={{ fontWeight: 800, color: "var(--green)" }}>Current: {form.feedback_score}%</span>
                   <span>Excellence (100)</span>
                </div>
              </div>
              <div className="form-field-full">
                <label style={{ fontSize: 12, color: "#666", marginBottom: 4, display: "block" }}>Date of Birth</label>
                <input type="date" value={form.date_of_birth || ""} onChange={(e)=>setForm({...form,date_of_birth:e.target.value})} />
              </div>
              <div className="form-field-full">
                <label style={{ fontSize: 12, color: "#666", marginBottom: 4, display: "block" }}>Date Joined</label>
                <input type="date" value={form.date_joined_company || ""} onChange={(e)=>setForm({...form,date_joined_company:e.target.value})} />
              </div>
              <textarea placeholder="Bio / Professional Summary" className="full-width" value={form.bio || ""} onChange={(e)=>setForm({...form,bio:e.target.value})} style={{ gridColumn: "1 / -1", minHeight: 80, padding: 8 }} />
              
              <div className="form-field-full" style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: "block" }}>Reporting Manager</label>
                <select 
                    value={form.reporting_to || ""} 
                    onChange={(e) => setForm({...form, reporting_to: e.target.value || null})}
                    style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}
                >
                    <option value="">No Manager (Top Level)</option>
                    {membersData
                        .filter(m => m.id !== editId) // Can't report to self
                        .map(m => (
                            <option key={m.id} value={m.id}>{m.name} (@{m.username})</option>
                        ))
                    }
                </select>
                <p style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>Setting a reporting manager enables hierarchical lead visibility.</p>
              </div>

              <div className="form-field-full" style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: "block" }}>Assign Workspace Roles</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, background: "#f8fafc", padding: 12, borderRadius: 12 }}>
                  {allRoles.map(role => (
                    <label key={role.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", background: "white", padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                      <input 
                        type="checkbox" 
                        checked={form.rbac_roles.includes(role.id)}
                        onChange={(e) => {
                          const current = [...form.rbac_roles];
                          if (e.target.checked) setForm({...form, rbac_roles: [...current, role.id]});
                          else setForm({...form, rbac_roles: current.filter(id => id !== role.id)});
                        }}
                      />
                      {role.name} {role.is_base_role && <small style={{ color: "#94a3b8" }}>(System)</small>}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowForm(false)}>Cancel</button>
              <button onClick={handleSave}>Save Employee</button>
            </div>
          </div>
        </div>
      )}

      {viewData && (
        <div className="modal-overlay" onClick={() => setViewData(null)}>
          <div className="view-modal" onClick={(e)=>e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
            <div className="view-header">
              {viewData.image ? <img src={viewData.image} className="view-avatar" alt="" /> : <div className="view-avatar">{initials(viewData.name)}</div>}
              <div>
                <h2>{viewData.name}</h2>
                <p className="view-role">{viewData.role}</p>
                <span className="view-badge">{viewData.dept}</span>
              </div>
            </div>
            <div className="view-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <p><strong>Email:</strong> {viewData.email}</p>
                    <p><strong>Phone:</strong> {viewData.phone_number || 'N/A'}</p>
                    <p><strong>Status:</strong> {STATUS_LABEL[viewData.status]}</p>
                    <p><strong>Joined:</strong> {viewData.date_joined_company || '---'}</p>
                </div>

                <div className="perf-insight" style={{ background: "#f8fafc", padding: "15px", borderRadius: "12px", margin: "15px 0" }}>
                   <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                      <strong>📊 Monthly Performance</strong>
                      <span style={{ color: "var(--green)", fontWeight: 800 }}>{viewData.performance}%</span>
                   </div>
                   <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", fontSize: "11px", textAlign: "center" }}>
                      <div style={{ background: "white", padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                         <div style={{ color: "#94a3b8", marginBottom: "3px" }}>Attendance</div>
                         <div style={{ fontWeight: 700 }}>{viewData.performance_breakdown?.attendance}%</div>
                      </div>
                      <div style={{ background: "white", padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                         <div style={{ color: "#94a3b8", marginBottom: "3px" }}>Tasks</div>
                         <div style={{ fontWeight: 700 }}>{viewData.performance_breakdown?.tasks}%</div>
                      </div>
                      <div style={{ background: "white", padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                         <div style={{ color: "#94a3b8", marginBottom: "3px" }}>Feedback</div>
                         <div style={{ fontWeight: 700 }}>{viewData.performance_breakdown?.feedback}%</div>
                      </div>
                   </div>
                </div>

                {/* Dynamic Onboarding Info */}
                <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#1e293b' }}>Onboarding Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                        {orgConfig?.fields?.map(fieldId => {
                            const standardFields = {
                                address: 'Address', state: 'State', pincode: 'Pincode',
                                date_of_birth: 'DOB', skills: 'Skills', bio: 'Bio'
                            };
                            if (!standardFields[fieldId]) return null;
                            return (
                                <p key={fieldId}><strong>{standardFields[fieldId]}:</strong> {viewData[fieldId] || '---'}</p>
                            );
                        })}
                    </div>

                    {orgConfig?.custom_questions?.length > 0 && (
                        <div style={{ marginTop: '15px' }}>
                            <h5 style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Custom Answers</h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {orgConfig.custom_questions.map(q => (
                                    <div key={q.id} style={{ background: '#f1f5f9', padding: '8px 12px', borderRadius: '8px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>{q.label}</div>
                                        <div style={{ fontSize: '13px' }}>
                                            {q.type === 'file' ? '📁 File Uploaded' : (viewData.custom_onboarding_data?.[q.id] || '---')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: '15px' }}>
                    <p><strong>Base Salary:</strong> <span className="salary-pill">₹{(Math.round(Number(viewData.base_salary) || 0)).toLocaleString()}</span></p>
                    <p><strong>Telephony Link:</strong> <code>{viewData.official_telephony_number || 'Not Linked'}</code></p>
                    <p><strong>Workspace Roles:</strong> <span className="view-badge secondary">{viewData.mapped_roles}</span></p>
                </div>
            </div>
            <div className="view-footer">
              <button onClick={() => setViewData(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}